import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge, Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type TemplateChannel } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { TEMPLATE_CHANNEL_LABELS, TEMPLATE_CHANNELS } from './templateContent.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const ROUTE = '/env/$environmentId/content/templates';

/** Content → Templates (BB-56): templates by channel with tags and search; a new template starts in the editor. */
export function TemplatesPage() {
  const { environmentId } = useParams({ from: ROUTE });
  const { api } = useRouteContext({ from: ROUTE });
  const navigate = useNavigate();
  const { has } = usePermissions();
  const canEdit = has('content.edit');
  const [channel, setChannel] = useState<'' | TemplateChannel>('');
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');
  const [archived, setArchived] = useState(false);
  const [newChannel, setNewChannel] = useState<TemplateChannel>('push');
  const filters = { ...(channel ? { channel } : {}), query, tag: tag.trim(), archived };
  const list = useQuery({ queryKey: ['templates', environmentId, filters], queryFn: () => api.listTemplates(environmentId, filters) });
  const filtered = Boolean(channel || query || tag.trim());

  return (
    <section>
      <h1>Templates</h1>
      <p className="hint">Reusable message content per channel. Inserting a template copies one version into the message; later versions never change messages already built.</p>
      {canEdit && (
        <div className="filters" role="group" aria-label="New template">
          <label>
            Channel{' '}
            <select aria-label="New template channel" value={newChannel} onChange={(e) => setNewChannel(e.target.value as TemplateChannel)}>
              {TEMPLATE_CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {TEMPLATE_CHANNEL_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          <Button variant="primary" onClick={() => void navigate({ to: '/env/$environmentId/content/templates/new', params: { environmentId }, search: { channel: newChannel } })}>
            New template
          </Button>
        </div>
      )}
      <div className="filters">
        <label>
          Channel{' '}
          <select aria-label="Filter by channel" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
            <option value="">All channels</option>
            {TEMPLATE_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {TEMPLATE_CHANNEL_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Search <input aria-label="Search templates" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label>
          Tag <input aria-label="Filter by tag" value={tag} onChange={(e) => setTag(e.target.value)} />
        </label>
        <label>
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Archived
        </label>
      </div>
      {list.isPending ? (
        <p aria-busy="true">Loading templates…</p>
      ) : list.isError ? (
        <ErrorState title="Templates could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title={archived ? 'No archived templates' : 'No templates yet'} description={filtered ? 'Nothing matches the filters.' : 'Create a template here or use «Save as template» in a composer.'} />
      ) : (
        <table aria-label="Templates">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Channel</th>
              <th scope="col">Version</th>
              <th scope="col">Tags</th>
              <th scope="col">Last edited</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to="/env/$environmentId/content/templates/$templateId" params={{ environmentId, templateId: t.id }}>
                    {t.name}
                  </Link>
                  {t.description && <div className="hint">{t.description}</div>}
                </td>
                <td>{TEMPLATE_CHANNEL_LABELS[t.channel]}</td>
                <td>v{t.currentVersion}</td>
                <td>
                  {t.tags.map((x) => (
                    <Badge key={x}>{x}</Badge>
                  ))}
                </td>
                <td>
                  {t.updatedAt.slice(0, 10)}
                  {t.updatedBy?.email ? ` · ${t.updatedBy.email}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
